import {
  Component,
  computed,
  effect,
  inject,
  input,
  Optional,
  signal,
  SkipSelf,
} from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  FormGroup,
  FormGroupDirective,
  FormGroupName,
  ReactiveFormsModule,
} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {DatePicker} from 'primeng/datepicker';
import {InputTextModule} from 'primeng/inputtext';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {PrimeNG} from 'primeng/config';
import {DatepickerLocaleUtils} from '../../datepicker-locale.util';

@Component({
  selector: 'app-form-field-datepicker',
  imports: [CommonModule, ReactiveFormsModule, DatePicker, InputTextModule, TranslocoPipe],
  templateUrl: './form-field-datepicker.component.html',
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: (parentGroupName: FormGroupName, parentFormDir: FormGroupDirective) =>
        parentGroupName ?? parentFormDir,
      deps: [
        [new Optional(), new SkipSelf(), FormGroupName],
        [new Optional(), new SkipSelf(), FormGroupDirective],
      ],
    },
  ],
})
export class FormFieldDatepickerComponent {
  private readonly primeng = inject(PrimeNG);
  private readonly translocoService = inject(TranslocoService);

  id = input.required<string>();
  fieldName = input.required<string>();
  label = input<string>('');
  showClear = input<boolean>(true);
  isReadOnly = input<boolean>(false);
  smallLabel = input<boolean>(false);

  view = input<'date' | 'month' | 'year'>('date');
  dateFormat = input<string>('dd.mm.yy');
  readOnlyFormat = input<string>('dd.MM.yyyy');

  // ---- signals -------
  readonly userLanguage = signal<string>(this.translocoService.getActiveLang());

  @Optional() private readonly controlContainer = inject(ControlContainer);

  get form(): FormGroup {
    return this.controlContainer?.control as FormGroup;
  }

  constructor() {
    effect(() => {
      this.primeng.setTranslation(DatepickerLocaleUtils.getLocale(this.userLanguage()));
    });
  }

  handleSelect(date: Date) {
    if (date instanceof Date && this.view() === 'month') {
      // Force to the 15th of the month at 12:00:00 to avoid any timezone shifts
      const adjustedDate = new Date(date.getFullYear(), date.getMonth(), 15, 12, 0, 0);
      this.form.get(this.fieldName())?.setValue(adjustedDate);
      console.log(`[DatePicker] Adjusted to 15th:`, adjustedDate);
    }
  }

  readonly isRequired = computed(() => {
    const control = this.form?.get(this.fieldName());
    if (!control || !control.validator) return false;
    const validator = control.validator({value: ''} as AbstractControl);
    return !!validator?.['required'];
  });
}
